const REMOTE_SRC = /^https?:\/\//i;

// Only images that opted in with crossOrigin can be read back from a canvas.
// Everything else (Google Static Maps, tour operator logos) keeps its remote
// URL.
const INLINABLE = 'img[crossorigin]';

const DEFAULT_MAX_WIDTH = 1600;
const DEFAULT_QUALITY = 0.8;
const DEFAULT_PIXEL_RATIO = 2;

export function targetWidthFor(image, { maxWidth, pixelRatio }) {
  const displayedWidth = image.getBoundingClientRect().width;
  const wanted =
    displayedWidth > 0 ? Math.round(displayedWidth * pixelRatio) : maxWidth;

  return Math.min(wanted, maxWidth, image.naturalWidth);
}

async function encodeToJpegDataUrl(image, width, quality) {
  const document = image.ownerDocument;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = Math.max(
    1,
    Math.round((image.naturalHeight * width) / image.naturalWidth),
  );

  const context = canvas.getContext('2d');
  context.imageSmoothingQuality = 'high';
  // JPEG has no alpha channel: without this, transparent pixels turn black.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  );
  if (!blob) {
    throw new Error('canvas.toBlob() returned no blob');
  }

  const { FileReader } = document.defaultView;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Downscales every inlinable, fully-loaded remote image of a document to a
 * data: URI, keyed by original src.
 *
 * Chrome embeds the source bitmap at its natural resolution when printing to
 * PDF, whatever CSS box it is displayed in — a 4000px photo shown 220px tall
 * still ships 4000px of JPEG. Downscaling is what makes the travel diary small
 * enough to email.
 *
 * A picture used both as a cover and a thumbnail is encoded once, at the
 * largest size any of its usages needs. Images that can't be read back (tainted
 * canvas because the bucket has no CORS header, encoder failure) are skipped —
 * they keep their remote URL.
 */
async function buildDownscaledMap(document, options = {}) {
  const {
    maxWidth = DEFAULT_MAX_WIDTH,
    quality = DEFAULT_QUALITY,
    pixelRatio = DEFAULT_PIXEL_RATIO,
    encode = encodeToJpegDataUrl,
  } = options;

  const toEncode = new Map();
  document.querySelectorAll(INLINABLE).forEach((image) => {
    const src = image.getAttribute('src');
    if (!src || !REMOTE_SRC.test(src)) {
      return;
    }
    // Still loading, or failed to load (a missing CORS header lands here too).
    if (!image.complete || !image.naturalWidth) {
      return;
    }

    const width = targetWidthFor(image, { maxWidth, pixelRatio });
    const known = toEncode.get(src);
    if (!known || known.width < width) {
      toEncode.set(src, { image, width });
    }
  });

  const dataUrls = new Map();
  for (const [src, { image, width }] of toEncode) {
    try {
      dataUrls.set(src, await encode(image, width, quality));
    } catch (error) {
      // skip: keep the remote URL
    }
  }

  return dataUrls;
}

/**
 * Swap the live document's images for downscaled data: URIs in
 * place, so window.print() embeds right-sized bitmaps instead of the originals.
 * The data URI keeps the same CSS box, so nothing reflows and the page-break
 * measurements stay valid.
 *
 * Returns a restore function that puts the original srcs back — call it after
 * printing so the on-screen page returns to full resolution.
 */
export async function downscaleLiveImagesForPrint(document, options = {}) {
  const dataUrls = await buildDownscaledMap(document, options);

  const restores = [];
  document.querySelectorAll(INLINABLE).forEach((image) => {
    const src = image.getAttribute('src');
    const dataUrl = dataUrls.get(src);
    if (dataUrl) {
      restores.push([image, src]);
      image.setAttribute('src', dataUrl);
    }
  });

  // Let the swapped-in bitmaps decode before the caller prints.
  await Promise.all(
    restores.map(([image]) =>
      image.decode ? image.decode().catch(() => {}) : Promise.resolve(),
    ),
  );

  return () => {
    restores.forEach(([image, src]) => image.setAttribute('src', src));
  };
}
